import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";
import Spinner from "@cs/components/src/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@cs/components/src/Banner";
import ChangesTrigger from "@cs/components/src/ChangesTrigger";
import VariableInputForm from "../shared/VariableInputForm";

import { Trash2, Save, XCircle, X } from "react-feather";
import Button from "@cs/components/src/Button";
import Checkbox from "@cs/components/src/Checkbox";
import JobList from "../jobs/JobList";
import { styled } from "styled-components";
import { PluginInstanceComponentSubgroups } from "./PluginInstanceComponentList";
import Section from "@cs/components/src/Section";
import Tabs from "@cs/components/src/Tabs";
import { Status } from "./PluginInstanceEditScreen";
import { developerToolsEnabled } from "../../services/env";

interface Props {
  editedPluginInstances: Record<string, PluginInstanceItem>;
  onChange: (store: PluginInstanceItem) => void;
  onRemove: (id: string) => void;
  onStatusChange: (id: string, status: string, message?: string) => void;
  statuses: Record<string, Status>;
}

// "components" tab was absorbed into the settings view — fall back to settings
// if a stale session had it remembered.
let rememberedTab = "settings";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: 5px;
  }
`;

/** Border-below instead of \`Tabs\`' own filled-background stripe, scoped to just this
 *  section-level usage (Tabs rendered as a Section's \`subTitle\`) rather than changed globally. */
const SectionTabs = styled(Tabs)`
  background: transparent;
  border-bottom: 1px solid var(--dark300);
`;

const DevModeWrapper = styled.div`
  padding: 10px 15px 0;
`;

const PluginDescription = styled.div`
  color: var(--gray100);
  font-size: 14px;
  padding: 10px 10px 0;
  white-space: pre-wrap;
`;

const PluginInstanceForm = (props: Props) => {
  const {
    onChange,
    onStatusChange,
    editedPluginInstances: pluginInstances,
    onRemove,
    statuses,
  } = props;
  const { pluginId, systemDomainId } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addComponentsJob } = useUpdateComponentsJob();
  // Guard: "components" tab was removed; fall back to settings if remembered.
  const [activeTab, setActiveTab] = useState(
    rememberedTab === "components" ? "settings" : rememberedTab
  );

  const editedPluginInstance = pluginInstances[pluginId ?? ""];
  const pluginKey = pluginId ?? "";

  // Per-plugin state bundled in one useState so the hook count/order stays
  // identical to the original code. React Router reuses this component instance
  // across plugin navigations, so we key state by pluginId.
  const [perPluginStateMap, setPerPluginStateMap] = useState<
    Record<string, { doRemove: boolean; cancelCount: number }>
  >({});
  const perPlugin = perPluginStateMap[pluginKey];
  // Incremented on Cancel to force VariableInputForm/RepeatableGroup to remount.
  const cancelCount = perPlugin?.cancelCount ?? 0;

  const setDoRemove = (value: boolean) => {
    setPerPluginStateMap((prev) => ({
      ...prev,
      [pluginKey]: {
        ...(prev[pluginKey] ?? { cancelCount: 0 }),
        doRemove: value,
      },
    }));
  };

  const setCancelCount = (fn: (c: number) => number) => {
    setPerPluginStateMap((prev) => {
      const prevState = prev[pluginKey];
      return {
        ...prev,
        [pluginKey]: {
          doRemove: prevState?.doRemove ?? true,
          cancelCount: fn(prevState?.cancelCount ?? 0),
        },
      };
    });
  };

  // ★ MODEL CHANGE: no edit mode — form is always editable.
  // Baseline map: keyed by pluginId so each plugin tracks its own saved state.
  // Mutations don't trigger re-renders (intentional — same semantics as useRef).
  const baselineMapRef = useRef<Record<string, PluginInstanceItem | null>>({});

  // True = server-existing plugin (show Remove, Cancel reverts).
  // False = new unsaved plugin (Cancel discards + navigates away).
  // Fallback: baseline seeded (server instance loaded) OR no edits yet.
  // Never falls back to !pluginInstances[pluginKey] — that flips to false as
  // soon as any edit is stored, which is the wrong signal.
  const doRemove =
    perPlugin?.doRemove ??
    (baselineMapRef.current[pluginKey] != null || !editedPluginInstance);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["pluginInstance", systemDomainId, pluginId],
    queryFn: async () => {
      if (editedPluginInstance) {
        const [configResponse, instanceResponse] = await Promise.all([
          ApiService.fetch<{ data: PluginConfigItem }>(
            "GET",
            `/api/config/plugin/${editedPluginInstance.pluginConfigId}`
          ),
          // Fetch fresh instance to seed baseline when returning to an existing
          // plugin that already has unsaved edits (else branch never ran).
          // Skip for new unsaved instances — they don't exist on the server yet.
          doRemove && baselineMapRef.current[pluginKey] == null
            ? ApiService.fetch<{
                data: {
                  pluginConfig: PluginConfigItem;
                  pluginInstance: PluginInstanceItem;
                };
              }>("GET", `/api/plugin_instance/${systemDomainId}/${pluginId}`)
            : null,
        ]);
        if (
          instanceResponse !== null &&
          baselineMapRef.current[pluginKey] == null
        ) {
          baselineMapRef.current[pluginKey] =
            instanceResponse.data.pluginInstance;
        }
        return { pluginConfig: configResponse.data, pluginInstance: undefined };
      } else {
        const response = await ApiService.fetch<{
          data: {
            pluginConfig: PluginConfigItem;
            pluginInstance: PluginInstanceItem;
          };
        }>("GET", `/api/plugin_instance/${systemDomainId}/${pluginId}`);
        // Seed baseline from server on first load (only once per plugin).
        if (baselineMapRef.current[pluginKey] == null) {
          baselineMapRef.current[pluginKey] = response.data.pluginInstance;
        }
        return response.data;
      }
    },
  });

  const pluginInstance = editedPluginInstance ?? data?.pluginInstance;
  const pluginConfig = data?.pluginConfig;

  const isReady = !isLoading && !isError && !!pluginConfig && !!pluginInstance;

  // Unsaved-changes detection: compare current state to baseline.
  const baseline = baselineMapRef.current[pluginKey] ?? null;
  const hasUnsavedChanges =
    isReady &&
    !!baseline &&
    JSON.stringify(pluginInstance) !== JSON.stringify(baseline);

  const saveMutation = useMutation({
    mutationFn: async () => {
      setDoRemove(true);
      const res = await ApiService.fetch<{
        scriptExecutionId: string;
        systemDomainId: string;
      }>(
        "PUT",
        `/api/plugin_instance/${systemDomainId}/${pluginInstance.id}`,
        pluginInstance
      );
      queryClient.invalidateQueries({
        queryKey: ["pluginInstanceList", systemDomainId],
      });
      return res;
    },
    onError: () =>
      onStatusChange(pluginInstance.id, "error", "Error saving plugin"),
    onSuccess: (data) => {
      // Update baseline to the newly saved state.
      baselineMapRef.current[pluginKey] = pluginInstance;
      onStatusChange(pluginInstance.id, "success");
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      );
    },
  });

  const abortAllMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/script_execution/abort/all/${systemDomainId}/${pluginInstance?.id}`
      );
      queryClient.invalidateQueries({ queryKey: ["jobList"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await ApiService.fetch<{
        scriptExecutionId: string;
        systemDomainId: string;
      }>(
        "DELETE",
        `/api/plugin_instance/${systemDomainId}/${pluginInstance.id}`
      );
      queryClient.invalidateQueries({
        queryKey: ["pluginInstanceList", systemDomainId],
      });
      onRemove(pluginInstance.id);
      navigate(`..`);
      return res;
    },
    onError: () =>
      onStatusChange(pluginInstance.id, "error", "Error removing plugin"),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  const handleTabChange = (tab: string) => {
    rememberedTab = tab;
    setActiveTab(tab);
  };

  const status = pluginInstance ? statuses[pluginInstance.id] : undefined;

  const hasRequiredFieldsMissing =
    isReady &&
    pluginConfig!.variables.some((variable) => {
      if (!variable.required) {
        return false;
      }
      const variableId = variable.id ?? variable.key;
      const instance = pluginInstance.variables.find(
        ({ variableConfigId, dataType }) =>
          variableConfigId === variableId && dataType === variable.dataType
      );
      const value = instance?.value;
      if (value === undefined || value === null || value === "") {
        return true;
      }
      if (Array.isArray(value) && value.length === 0) {
        return true;
      }
      return false;
    });

  return (
    <Section
      title={
        isReady ? (
          <TitleRow>
            <span>{pluginConfig!.title}</span>
          </TitleRow>
        ) : (
          "Loading..."
        )
      }
      subTitle={
        <SectionTabs
          tabs={[
            {
              key: "settings",
              label: "Settings",
              color: hasRequiredFieldsMissing ? "var(--orange300)" : undefined,
            },
            { key: "jobs", label: "Jobs" },
          ]}
          value={activeTab}
          onChange={isReady ? handleTabChange : undefined}
        />
      }
      buttons={
        <>
          {isReady && status?.status && (
            <ChangesTrigger
              $tone={
                status.status === "error"
                  ? "error"
                  : status.status === "dirty"
                  ? "pending"
                  : "success"
              }
            >
              {status.status === "error"
                ? status.message || "Unexpected error"
                : status.status === "dirty"
                ? "Unsaved changes"
                : "Saved"}
            </ChangesTrigger>
          )}

          {activeTab === "jobs" && isReady && (
            <>
              {abortAllMutation.isError && (
                <Banner error="Failed to abort all jobs" />
              )}
              <Button
                label="Abort All"
                type="danger"
                icon={XCircle}
                borderless
                onClick={() => abortAllMutation.mutate()}
                loading={abortAllMutation.isLoading}
              />
            </>
          )}
          {activeTab === "settings" && (
            <>
              {doRemove && (
                <Button
                  label="Remove"
                  icon={Trash2}
                  type="danger"
                  borderless
                  disabled={!isReady}
                  onClick={() => deleteMutation.mutate()}
                  loading={deleteMutation.isLoading}
                />
              )}
              {/* Cancel: for new instances, always enabled and discards; for
                  existing instances, reverts to server state when dirty */}
              <Button
                label="Cancel"
                icon={X}
                type="danger"
                borderless
                disabled={!isReady || (doRemove && !hasUnsavedChanges)}
                onClick={() => {
                  if (!doRemove) {
                    // New unsaved instance — discard and navigate back.
                    onRemove(pluginInstance!.id);
                    navigate("..");
                  } else {
                    // Existing instance — clear all edits, re-fetch from server.
                    baselineMapRef.current[pluginKey] = null;
                    onRemove(pluginInstance!.id);
                    queryClient.invalidateQueries([
                      "pluginInstance",
                      systemDomainId,
                      pluginId,
                    ]);
                    setCancelCount((c) => c + 1);
                  }
                }}
              />
              {/* Save: always visible since form is always editable */}
              <Button
                label="Save"
                icon={Save}
                type="success"
                disabled={!isReady}
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isLoading}
              />
            </>
          )}
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <Banner error="Error loading" />
      ) : isReady ? (
        <>
          {activeTab === "settings" && (
            <>
              {/* Dev mode at top (#5), above description */}
              {developerToolsEnabled && (
                <DevModeWrapper>
                  <Checkbox
                    inlineLabel
                    label="Development Mode"
                    value={pluginInstance!.development ?? false}
                    onChange={(value) => {
                      onStatusChange(pluginInstance!.id, "dirty");
                      onChange({ ...pluginInstance!, development: value });
                    }}
                  />
                </DevModeWrapper>
              )}
              {pluginConfig!.description && (
                <PluginDescription>
                  {pluginConfig!.description}
                </PluginDescription>
              )}
              <VariableInputForm
                key={`vif-${cancelCount}`}
                value={pluginInstance!}
                config={pluginConfig!}
                variableLabels={pluginInstance!.variableLabels}
                onChange={(value) => {
                  onStatusChange(pluginInstance!.id, "dirty");
                  onChange(value);
                }}
              />
              {/* Component subgroups — each component as a collapsible section
                  appended below the settings fields (replaces Components tab). */}
              <PluginInstanceComponentSubgroups
                key={`pics-${cancelCount}`}
                pluginConfig={pluginConfig!}
                pluginInstance={pluginInstance!}
                onChange={(value) => {
                  onStatusChange(pluginInstance!.id, "dirty");
                  onChange(value);
                }}
              />
            </>
          )}
          {activeTab === "jobs" && (
            <JobList
              systemDomainId={systemDomainId}
              instanceId={pluginInstance!.id}
              modal
            />
          )}
        </>
      ) : null}
    </Section>
  );
};

export default PluginInstanceForm;
